import CloseIcon from "@mui/icons-material/Close";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Box,
  Typography
} from "@mui/material";
import React, { useState, useRef } from "react";
import { createRemovalRequest } from "../shared";
import { BlacklistEntityType, CreateRemovalRequestDto, RemovalRequest, RemovalRequestDto } from "../types";
import Iconify from "../../../minimals/components/iconify";

type CreateRemovalRequestModalProps = {
  open: boolean;
  onClose: () => void;
  entityId: number;
  entityType: BlacklistEntityType;
  onActionComplete?: () => Promise<void>;
};

const CreateRemovalRequestModal = ({
  open,
  onClose,
  entityId,
  entityType,
  onActionComplete
}: CreateRemovalRequestModalProps) => {
  const [reason, setReason] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileName, setFileName] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];
      if (file.type === "application/pdf") {
        setSelectedFile(file);
        setFileName(file.name);
      } else {
        alert("Please upload a PDF file");
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      }
    }
  };

  const convertFileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const base64String = reader.result as string;
        const base64Content = base64String.split(",")[1];
        resolve(base64Content);
      };
      reader.onerror = (error) => reject(error);
    });
  };

  const handleSubmit = async () => {
    if (!reason) {
      alert("Please provide a reason for removal.");
      return;
    }

    if (!selectedFile) {
      alert("Please upload a proof document (PDF).");
      return;
    }

    setIsSubmitting(true);
    try {
      const base64File = await convertFileToBase64(selectedFile);
      const removalRequestDto: CreateRemovalRequestDto = {
        removalRequest: { entityId, entityType, reason } as RemovalRequest,
        proofDocumentBase64: base64File,
        proofDocumentFileName: fileName
      };

      await createRemovalRequest(removalRequestDto);

      if (onActionComplete) {
        await onActionComplete();
      }

      onClose();
    } catch (error) {
      console.error("Error creating removal request:", error);
      alert("An error occurred while submitting your request. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Request Removal
        <IconButton onClick={onClose} sx={{ position: "absolute", right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <TextField
          label="Reason for Removal"
          multiline
          rows={4}
          fullWidth
          margin="normal"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />

        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Upload Proof Document (PDF)
          </Typography>

          <input
            type="file"
            accept="application/pdf"
            onChange={handleFileChange}
            style={{ display: "none" }}
            ref={fileInputRef}
          />

          <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
            <Button
              variant="outlined"
              onClick={handleBrowseClick}
              startIcon={<Iconify icon="eva:file-add-outline" />}
              sx={{ mr: 2 }}
            >
              Browse
            </Button>
            <Typography variant="body2" color={selectedFile ? "text.primary" : "text.secondary"}>
              {selectedFile ? selectedFile.name : "No file selected"}
            </Typography>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={isSubmitting}>
          {isSubmitting ? "Submitting..." : "Submit"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateRemovalRequestModal;
